import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash2, ChevronRight, MapPin, Phone, Mail, CalendarDays, Plus, Pencil, Briefcase, Box, Download } from "lucide-react";
import { ExportKlienta } from "../components/PrevodKlienta";
import EmptyState from "../components/EmptyState";
import AktivitaDetail from "../components/AktivitaDetail";
import ClientForm from "../components/ClientForm";
import ZarizeniForm from "../components/ZarizeniForm";
import NovyZapisDialog from "../components/NovyZapisDialog";
import NovaAktivita from "../components/NovaAktivita";
import PripadForm from "../components/PripadForm";
import { Spinner } from "../components/ui/kit";
import { FazePill, etapa } from "../lib/faze";
import { fmtKc } from "../lib/format";
import { fmtYmd } from "../lib/servis";
import { useAuth } from "../context/AuthContext";
import { SwipeAktivita, SwipeNapoveda, useAktivitaSwipe } from "../components/AktivitaSwipe";
import { canSee, canEditAktivita } from "../lib/nav";
import { useJeMobil } from "../lib/mobil";
import api, { apiError } from "../lib/api";
import { SkupinyAktivit } from "../lib/aktivity";
import { Drobecky, useNazevStranky, useUrlStav } from "../lib/navigace";
import TymKlienta from "../components/TymKlienta";
import DetailHlava, { KartaZalozky, KartaSekce, Pridat, StitekTmavy } from "../components/DetailHlava";
import RadekAktivity from "../components/RadekAktivity";
import SlozkaDokumentu from "../components/SlozkaDokumentu";

const TABS = [
  { key: "aktivity", label: "Aktivity", pocet: "aktivity" },
  { key: "obchod", label: "Obchod", pocet: "obchod" },
  { key: "servis", label: "Servis", pocet: "servis" },
];

/** „3/2023" — u data vzniku klienta nikoho nezajímá den. */
function mesicRok(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getFullYear()}`;
}

/** „14. 9." — nejbližší servis je vždycky letos nebo za pár měsíců. */
function denMesic(ymd) {
  if (!ymd) return "—";
  const [, m, d] = ymd.split("-");
  return `${Number(d)}. ${Number(m)}.`;
}

function Chip({ children, tón = "sedy" }) {
  const barvy = {
    zeleny: "text-success bg-success-bg",
    zluty: "text-[#7a4c04] bg-[#fdf6e8]",
    sedy: "text-sec-3 bg-[#f2f2ef]",
  };
  return (
    <span className={`label-up px-2.5 py-1 rounded-pill shrink-0 ${barvy[tón]}`}>{children}</span>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canWrite = canSee(user, ["OWNER", "OBCHODNIK"]);
  const isOwner = canSee(user, ["OWNER"]);
  const isSub = (user?.roles || []).includes("SUBDODAVATEL") && !user?.superadmin;

  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [client, setClient] = useState(undefined);
  const [prehled, setPrehled] = useState(null);
  const [tab, setTab] = useUrlStav("tab", "aktivity");
  const [acts, setActs] = useState(null);
  const [edit, setEdit] = useState(false);
  // Export klienta souborem: lidé firmy ano, subdodavatel ne (hlídá i server).
  const smiExport = canSee(user, ["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA"]);
  const [exportOkno, setExportOkno] = useState(false);
  const [newAct, setNewAct] = useState(false);
  const [cases, setCases] = useState(null);
  const [newCase, setNewCase] = useState(false);
  const [devices, setDevices] = useState(null);
  const [servisRozprac, setServisRozprac] = useState([]);
  const [newDevice, setNewDevice] = useState(false);
  const [newZapis, setNewZapis] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/clients/${id}`);
      setClient(data);
    } catch {
      setClient(null);
    }
  }, [id]);

  const loadPrehled = useCallback(() => {
    api.get(`/api/clients/${id}/prehled`).then(({ data }) => setPrehled(data)).catch(() => setPrehled(null));
  }, [id]);

  const loadActs = useCallback(async () => {
    const { data } = await api.get("/api/aktivity", { params: { client_id: id, limit: 200 } });
    setActs(data.items || []);   // řazení a skupiny: SkupinyAktivit
  }, [id]);

  useEffect(() => { load(); loadActs(); loadPrehled(); }, [load, loadActs, loadPrehled]);

  // Lidé pro tým klienta (kolegové + subdodavatelé) — jen pro ty, kdo tým mění.
  const [lide, setLide] = useState([]);
  useEffect(() => {
    if (!canWrite) return;
    api.get("/api/team").then((r) => setLide((r.data.members || []).filter((m) => m.active))).catch(() => {});
  }, [canWrite]);
  const ulozitTym = async (ids) => {
    try { await api.patch(`/api/clients/${id}`, { tym_ids: ids }); await load(); }
    catch (e) { throw new Error(apiError(e.response?.data?.detail) || e.message); }
  };
  useNazevStranky(client?.display_name);

  const loadCases = useCallback(async () => {
    const { data } = await api.get("/api/obchodni-pripady", { params: { client_id: id, limit: 100 } });
    setCases(data.items);
  }, [id]);
  useEffect(() => { if (tab === "obchod") loadCases(); }, [tab, loadCases]);

  const loadServis = useCallback(async () => {
    const [dev, zap] = await Promise.all([
      api.get("/api/zarizeni", { params: { client_id: id, limit: 200 } }),
      api.get("/api/zapisy", { params: { client_id: id, stav: "ROZPRACOVANY", limit: 100 } }).catch(() => ({ data: { items: [] } })),
    ]);
    setDevices(dev.data.items || []);
    setServisRozprac(zap.data.items || []);
  }, [id]);
  useEffect(() => { if (tab === "servis") loadServis(); }, [tab, loadServis]);

  const toggle = async (a) => {
    await api.post(`/api/aktivity/${a.id}/hotovo`);
    loadActs();
  };
  const mobil = useJeMobil();
  const swipe = useAktivitaSwipe({ reload: () => { loadActs(); loadPrehled(); }, onUpravit: setEditAct });

  const remove = async () => {
    if (!window.confirm("Opravdu archivovat tohoto klienta?")) return;
    await api.delete(`/api/clients/${id}`);
    nav("/klienti");
  };

  if (client === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (client === null)
    return <div className="card p-8"><EmptyState icon="UserX" title="Klient nenalezen"
      description="Klient neexistuje nebo byl archivován."
      action={<button onClick={() => nav("/klienti")} className="btn-ghost">Zpět na klienty</button>} /></div>;

  const a = client.adresa || {};
  const adresa = [a.ulice, [a.psc, a.mesto].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const muzeCeny = prehled?.muze_ceny;
  const otevreny = prehled?.otevreny_pripad;
  const servis = prehled?.nejblizsi_servis;
  const pocty = prehled?.pocty || {};

  // Akce vpravo v liště záložek se mění podle toho, na co se člověk dívá.
  const akceTabu = {
    aktivity: { text: "Aktivita", fn: () => setNewAct(true), testid: "client-new-activity" },
    obchod: canWrite ? { text: "Nový případ", fn: () => setNewCase(true), testid: "client-new-case" } : null,
    servis: isSub ? null : { text: "Zařízení", fn: () => setNewDevice(true), testid: "client-new-device" },
  }[tab];
  const dotazAdresa = [a.ulice, [a.psc, a.mesto].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  return (
    <div data-testid="client-detail" className="flex flex-col gap-4">
      <Drobecky cesta={[{ nazev: "Klienti", to: "/klienti", testid: "client-zpet" }]}
        aktualni={`${client.display_name}${a.mesto ? `, ${a.mesto}` : ""}`} />

      <DetailHlava testid="client-hlava" druh="Klient" nazev={client.display_name}
        stitky={<>
          {client.status !== "NEAKTIVNI" && <StitekTmavy barva="#5fd3a5">Aktivní klient</StitekTmavy>}
          {client.servisni_smlouva && <StitekTmavy>Servisní smlouva</StitekTmavy>}
        </>}
        meta={[
          adresa && <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{adresa}</span>,
          client.telefon && <a href={`tel:${client.telefon}`} className="amount inline-flex items-center gap-1.5 hover:text-white"><Phone size={13} />{client.telefon}</a>,
          client.email && <a href={`mailto:${client.email}`} className="inline-flex items-center gap-1.5 hover:text-white"><Mail size={13} />{client.email}</a>,
          client.partner && (
            <button onClick={() => nav(`/partneri/${client.partner.id}`)} className="hover:text-white" data-testid="client-partner-link">
              Od partnera <span className="font-600 text-white">{client.partner.nazev}</span>
            </button>
          ),
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} />Klient od <span className="amount">{mesicRok(client.created_at)}</span></span>,
        ].filter(Boolean)}
        pod={<TymKlienta tmavy clenove={client.tym || []} lide={lide} smiMenit={canWrite} smiSub={isOwner}
          mujId={user?.id} onUlozit={ulozitTym} />}
        vedlejsi={canWrite ? [{ text: "Upravit", onClick: () => setEdit(true), testid: "client-edit" }] : []}
        hlavni={{ text: "Nová aktivita", ikona: Plus, onClick: () => setNewAct(true), testid: "client-nova-aktivita" }}
        vice={[
          ...(canWrite ? [{ text: "Upravit klienta", ikona: Pencil, onClick: () => setEdit(true), testid: "client-vice-upravit" }] : []),
          ...(smiExport ? [{ text: "Exportovat pro subdodavatele", ikona: Download, onClick: () => setExportOkno(true), testid: "client-vice-export" }] : []),
          ...(isOwner ? [{ text: "Archivovat klienta", ikona: Trash2, nebezpecne: true, onClick: remove, testid: "client-delete" }] : []),
        ]}
        cta={[
          { text: "Aktivita", ikona: Plus, onClick: () => setNewAct(true), testid: "client-cta-aktivita" },
          ...(client.telefon ? [{ text: "Zavolat", ikona: Phone, href: `tel:${client.telefon}`, testid: "client-cta-zavolat" }] : []),
          ...(dotazAdresa ? [{ text: "Navigovat", ikona: MapPin, href: `https://mapy.com/zakladni?q=${encodeURIComponent(dotazAdresa)}`, testid: "client-cta-navigovat" }] : []),
        ]}
        /* Peníze jen pro toho, kdo je smí vidět. Obrat je to, co si klient
           objednal; otevřené případy jsou vedle, ať se zakázka nepočítá dvakrát. */
        dlazdice={[
          ...(muzeCeny ? [{ popisek: "Obrat celkem", hodnota: fmtKc(prehled.obrat_kc || 0), jednotka: "Kč", testid: "dl-obrat",
            pod: "objednané zakázky" }] : []),
          { popisek: "Otevřený případ", testid: "dl-otevreny",
            hodnota: muzeCeny ? fmtKc(prehled?.otevreno_kc || 0) : (otevreny ? "1" : "—"), jednotka: muzeCeny ? "Kč" : "",
            onClick: otevreny ? () => nav(`/obchod/${otevreny.id}`) : undefined,
            pod: otevreny ? [otevreny.faze_label, ...(otevreny.technologie || [])].filter(Boolean).join(" · ") : "žádný otevřený případ" },
          { popisek: "Nejbližší servis", testid: "dl-servis", hodnota: denMesic(servis?.datum),
            zvyraznit: servis?.po_terminu ? "danger" : undefined,
            pod: servis ? servis.nazev : "nic naplánovaného" },
        ]} />

      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        <KartaZalozky testid="client-zalozky" aktivni={tab} onZmena={setTab}
          zalozky={TABS.map((t) => ({ key: t.key, label: t.label, pocet: pocty[t.pocet] ?? null }))}
          akce={akceTabu && <Pridat text={akceTabu.text} onClick={akceTabu.fn} testid={akceTabu.testid} />}>
          {tab === "aktivity" && (
            acts === null ? <div className="p-8 flex justify-center text-muted"><Spinner /></div>
              : acts.length === 0 ? (
                <EmptyState icon="CalendarPlus" title="Zatím žádné aktivity"
                  description="Naplánuj schůzku, hovor nebo servisní prohlídku pro tohoto klienta." />
              ) : mobil ? (
                <div className="p-3 space-y-2" data-testid="aktivity-swipe">
                  <SwipeNapoveda smiUpravit={acts.some((x) => canEditAktivita(user, x.oblast))} />
                  <SkupinyAktivit kompakt items={acts} render={(x) => (
                    <SwipeAktivita key={x.id} a={x} onOpen={(y) => setDetailId(y.id)}
                      smiUpravit={canEditAktivita(user, x.oblast)} {...swipe.swipeProps} />
                  )} />
                </div>
              ) : <SkupinyAktivit items={acts} render={(x) => (
                <RadekAktivity key={x.id} a={x} onOpen={() => setDetailId(x.id)}
                  onToggle={canEditAktivita(user, x.oblast) ? () => toggle(x) : null} />
              )} />
          )}

          {tab === "obchod" && (
            cases === null ? <div className="p-8 flex justify-center text-muted"><Spinner /></div>
              : cases.length === 0 ? (
                <EmptyState icon="Briefcase" title="Žádné obchodní případy"
                  description="Založ první případ pro tohoto klienta." />
              ) : cases.map((c) => (
                <button key={c.id} onClick={() => nav(`/obchod/${c.id}`)} data-testid={`client-case-${c.id}`}
                  className="w-full text-left flex items-center gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0 hover:bg-inputbg">
                  <span className="h-9 w-9 rounded-[12px] bg-inputbg text-sec inline-flex items-center justify-center shrink-0"><Briefcase size={16} strokeWidth={1.9} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block t-telo font-600 text-ink truncate">{c.nazev}</span>
                    <span className="block t-meta text-muted truncate">
                      <span className="amount">{c.cislo}</span>
                      {(c.technologie_chips || []).length ? ` · ${c.technologie_chips.join(" · ")}` : ""}
                    </span>
                  </span>
                  {c.hodnota !== undefined && (
                    <span className="amount t-telo font-700 text-ink hidden sm:block">{fmtKc(c.hodnota)} Kč</span>
                  )}
                  <FazePill faze={etapa(c)} />
                </button>
              ))
          )}

          {tab === "servis" && (
            <>
              {devices === null ? <div className="p-8 flex justify-center text-muted"><Spinner /></div>
                : devices.length === 0 ? (
                  <EmptyState icon="Wrench" title="Žádná zařízení"
                    description="Přidej první zařízení v péči — TČ, rekuperaci, FVE nebo klimatizaci." />
                ) : devices.map((d) => (
                  <button key={d.id} onClick={() => nav(`/servis/zarizeni/${d.id}`)} data-testid={`client-device-${d.id}`}
                    className="w-full text-left flex items-center gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0 hover:bg-inputbg">
                    <span className="h-9 w-9 rounded-[12px] bg-inputbg text-sec inline-flex items-center justify-center shrink-0"><Box size={16} strokeWidth={1.9} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block t-telo font-600 text-ink truncate">{d.nazev}</span>
                      <span className="block t-meta text-muted truncate">
                        {d.vyrobni_cislo ? <span className="amount">SN {d.vyrobni_cislo} · </span> : ""}
                        prohlídka{" "}
                        <span className={`amount ${d.po_terminu ? "text-danger font-600" : ""}`}>
                          {d.servisni_interval_mesice === 0 ? "bez servisu" : fmtYmd(d.dalsi_prohlidka)}
                        </span>
                      </span>
                    </span>
                    {d.otevrenych_nedostatku > 0
                      ? <Chip tón="zluty">{d.otevrenych_nedostatku} nedostatek</Chip>
                      : <Chip tón="zeleny">Bez závad</Chip>}
                    <ChevronRight size={15} className="text-muted-2 shrink-0" />
                  </button>
                ))}

              {servisRozprac.length > 0 && (
                <div className="px-4 md:px-5 py-3 bg-[#fafaf8] border-t border-line-soft">
                  <div className="label-up text-muted mb-2">Nedokončené servisní zápisy</div>
                  <div className="space-y-2">
                    {servisRozprac.map((z) => (
                      <button key={z.id} onClick={() => nav(`/servis/zapisy/${z.id}`)} data-testid={`client-rozprac-${z.id}`}
                        className="w-full text-left rounded-card bg-[#fdf8ec] border border-[#ebd7a8] px-3.5 py-2.5 flex items-center gap-3">
                        <span className="h-2 w-2 rounded-full bg-[#c77a0a] shrink-0" />
                        <span className="flex-1 min-w-0">
                          <span className="block t-telo font-600 text-ink truncate">{z.nazev || "Servisní zápis"}</span>
                          <span className="block t-meta text-[#7a4c04] amount">{fmtYmd(z.datum)}</span>
                        </span>
                        <span className="t-meta font-700 text-accent-hover shrink-0">Dokončit →</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!isSub && (
                <div className="px-4 md:px-5 py-3 border-t border-line-soft">
                  <Pridat text="Nový zápis" onClick={() => setNewZapis(true)} testid="client-new-zapis" />
                </div>
              )}
            </>
          )}
        </KartaZalozky>

        {/* pravý sloupec — kontext, který platí pro celou kartu */}
        <aside className="flex flex-col gap-4 min-w-0" data-testid="client-kontext">
          <KartaSekce titul="Adresa" telo>
            <MapaPlacholder adresa={a} />
            {adresa && <div className="t-meta text-sec mt-2.5">{adresa}</div>}
          </KartaSekce>
          {/* Dokumenty klienta zůstávají na disku firmy — tady jen odkaz na složku. */}
          <KartaSekce titul="Dokumenty" telo testid="client-dokumenty">
            <SlozkaDokumentu url={client.slozka_url} komu={client.slozka_komu || []} ai={client.slozka_ai}
              testid="client-slozka" popisek="Složka klienta"
              onUlozit={canWrite ? async (zmena) => {
                const { data } = await api.patch(`/api/clients/${id}`, zmena);
                setClient((c) => ({ ...c, slozka_url: data.slozka_url, slozka_komu: data.slozka_komu, slozka_ai: data.slozka_ai }));
              } : null} />
          </KartaSekce>
          <KartaSekce titul="Zařízení" pocet={(prehled?.zarizeni || []).length || null}
            akce={!isSub && <Pridat text="Přidat" onClick={() => setNewDevice(true)} testid="client-kontext-pridat-zarizeni" />}>
            <div className="px-4 md:px-5 pb-3">
              {(prehled?.zarizeni || []).length === 0 && (
                <div className="t-meta text-muted-2 pb-1">Žádné zařízení v péči.</div>
              )}
              {(prehled?.zarizeni || []).map((z) => (
                <button key={z.id} onClick={() => nav(`/servis/zarizeni/${z.id}`)}
                  data-testid={`kontext-zarizeni-${z.id}`}
                  className="w-full flex justify-between gap-3 py-2.5 border-b border-line-soft last:border-0 text-left t-meta md:text-[13px] text-sec hover:text-ink">
                  <span className="truncate">{z.nazev}</span>
                  <span className="amount font-700 text-ink shrink-0">{z.rok || "—"}</span>
                </button>
              ))}
            </div>
          </KartaSekce>
          {client.poznamky && (
            <KartaSekce titul="Poznámka" telo>
              <div className="t-telo md:text-[13.5px] leading-[1.55] text-sec whitespace-pre-line" data-testid="client-poznamka">
                {client.poznamky}
              </div>
            </KartaSekce>
          )}
        </aside>
      </div>

      {exportOkno && <ExportKlienta clientId={client.id} onClose={() => setExportOkno(false)} />}
      {edit && <ClientForm client={client} onClose={() => setEdit(false)} onSaved={() => { load(); loadPrehled(); }} />}
      {newAct && <NovaAktivita defaultClientId={id} onClose={() => setNewAct(false)} onSaved={() => { loadActs(); loadPrehled(); }} />}
      {newCase && <PripadForm clientId={id} client={client} onClose={() => setNewCase(false)} onSaved={(d) => nav(`/obchod/${d.id}`)} />}
      {newDevice && <ZarizeniForm clientId={id} onClose={() => setNewDevice(false)} onSaved={() => { loadServis(); loadPrehled(); }} />}
      {newZapis && <NovyZapisDialog clientId={id} onClose={() => setNewZapis(false)} />}
      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(x) => { setDetailId(null); setEditAct(x); }}
          onChanged={loadActs} />
      )}
      {editAct && (
        <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)}
          onSaved={() => { setEditAct(null); loadActs(); }} />
      )}
      {swipe.supliky}
    </div>
  );
}

/** Mapa podle adresy (vložená Google mapa, bez API klíče). Štítek dole
 *  otevře adresu v Mapy.com — odtud se naviguje. */
function MapaPlacholder({ adresa }) {
  const popis = [adresa.ulice, adresa.mesto].filter(Boolean).join(", ");
  const dotaz = [adresa.ulice, [adresa.psc, adresa.mesto].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const styl = {
    background: "repeating-linear-gradient(135deg,#eceae4 0 6px,#e4e2db 6px 12px)",
  };
  if (!popis) {
    return (
      <div className="h-[150px] rounded-[12px] flex items-end p-2.5" style={styl}>
        <span className="amount text-[10.5px] text-[#7e7a70] bg-white/85 px-1.5 py-0.5 rounded-[5px]">
          adresa neuvedená
        </span>
      </div>
    );
  }
  return (
    <div className="relative h-[150px] md:h-[170px] rounded-[12px] overflow-hidden" style={styl} data-testid="client-mapa">
      <iframe title={`Mapa: ${popis}`} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
        src={`https://maps.google.com/maps?q=${encodeURIComponent(dotaz)}&z=15&output=embed`}
        className="absolute inset-0 w-full h-full border-0" />
      <a href={`https://mapy.com/zakladni?q=${encodeURIComponent(dotaz)}`} target="_blank" rel="noreferrer"
        data-testid="client-mapa-odkaz"
        className="absolute left-2.5 bottom-2.5 max-w-[calc(100%-20px)] truncate amount text-[10.5px] text-[#4e545c] bg-white/90 hover:bg-white px-1.5 py-0.5 rounded-[5px] shadow-card">
        Otevřít v mapách · {popis}
      </a>
    </div>
  );
}
